import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useCreateOneOnOne, useGenerateAgenda, useManagers, useMe, useOneOnOneStats, useOneOnOnes, useOrgMe, useUpdateOneOnOne, type AgendaItem, type OneOnOne } from "../api/hooks";
import { Button, Chip, Empty, ErrorBox, Field, Loading, Page, Table, statusTone, type Tone } from "../components/ui";
import { fmtDateTime, title, plural } from "../lib/format";

export const Route = createFileRoute("/one-on-ones")({ component: OneOnOnes });

function OneOnOnes() {
  const me = useMe();
  const isAdmin = me.data?.roles.some((r) => r === "hr_admin" || r === "platform_admin") ?? false;
  return (
    <Page title="1:1s" sub={isAdmin ? "Operating view across managers: scheduled, completed, missed. Notes are private to each pair and never shown here." : "Your 1:1s: upcoming, previous, and what was agreed."}>
      {isAdmin && <Stats />}
      <Mine />
    </Page>
  );
}

function Stats() {
  const stats = useOneOnOneStats();
  const managers = useManagers();
  const org = useOrgMe();
  const nameOf = (key: string) => managers.data?.find((p) => p.person_key === key)?.full_name ?? (org.data?.me?.person_key === key ? org.data.me.full_name : null) ?? key;
  const deptOf = (key: string) => managers.data?.find((p) => p.person_key === key)?.department ?? "No department";
  if (stats.isPending) return <Loading />;
  if (stats.isError) return <ErrorBox error={stats.error} />;
  const rows = stats.data;
  const total = rows.reduce((a, s) => ({ c: a.c + s.completed, m: a.m + s.missed, s: a.s + s.scheduled }), { c: 0, m: 0, s: 0 });
  const all = total.c + total.m + total.s;
  const rate = (c: number, m: number) => (c + m > 0 ? Math.round((100 * c) / (c + m)) : null);
  const pct = rate(total.c, total.m);
  const missedPct = total.c + total.m > 0 ? Math.round((100 * total.m) / (total.c + total.m)) : null;
  const managerCount = managers.data?.length ?? 0;
  const adopting = rows.filter((s) => s.completed + s.missed + s.scheduled > 0).length;
  const adoption = managerCount > 0 ? Math.round((100 * adopting) / managerCount) : null;
  const byDept = new Map<string, { c: number; m: number; n: number }>();
  for (const s of rows) {
    const d = deptOf(s.manager_person_key);
    const cur = byDept.get(d) ?? { c: 0, m: 0, n: 0 };
    byDept.set(d, { c: cur.c + s.completed, m: cur.m + s.missed, n: cur.n + 1 });
  }
  const depts = [...byDept.entries()].map(([d, v]) => ({ dept: d, rate: rate(v.c, v.m), n: v.n })).sort((a, b) => (a.rate ?? 101) - (b.rate ?? 101));
  const lowest = rows.map((s) => ({ ...s, rate: rate(s.completed, s.missed) })).filter((s) => s.rate != null).sort((a, b) => a.rate! - b.rate! || b.missed - a.missed).slice(0, 5);
  const toneOf = (p: number | null): Tone => (p == null ? "neutral" : p >= 80 ? "good" : p >= 50 ? "elevated" : "high");
  const seg = (n: number) => (all > 0 ? (100 * n) / all : 0);
  return (
    <div className="mb-6">
      <div className="grid grid-cols-5 gap-3 mb-4">
        <div className="card kpi"><h4>1:1 completion rate</h4><div className="v">{pct != null ? `${pct}%` : "—"}{pct != null && <Chip tone={toneOf(pct)}>{pct >= 80 ? "On track" : pct >= 50 ? "Watch" : "Needs attention"}</Chip>}</div><div className="delta">{total.c} completed of {total.c + total.m} due, 90 days</div></div>
        <div className="card kpi"><h4>Scheduled 1:1s</h4><div className="v">{total.s}</div><div className="delta">upcoming across {plural(rows.length, "manager")}</div></div>
        <div className="card kpi"><h4>Missed 1:1s</h4><div className="v">{missedPct != null ? `${missedPct}%` : "—"}{missedPct != null && <Chip tone={missedPct <= 5 ? "good" : missedPct <= 15 ? "elevated" : "high"}>{missedPct <= 5 ? "Good" : missedPct <= 15 ? "Watch" : "Needs attention"}</Chip>}</div><div className="delta">{total.m} missed</div></div>
        <div className="card kpi"><h4>Manager adoption</h4><div className="v">{adoption != null ? `${adoption}%` : "—"}{adoption != null && <Chip tone={toneOf(adoption)}>{adoption >= 80 ? "Excellent" : adoption >= 50 ? "Growing" : "Low"}</Chip>}</div><div className="delta">{adopting} of {plural(managerCount, "manager")} hold 1:1s</div></div>
        <div className="card kpi"><h4>Completed</h4><div className="v">{total.c}</div><div className="delta">in the last 90 days</div></div>
      </div>
      <div className="grid gap-3 mb-4" style={{ gridTemplateColumns: "1fr 1.2fr 1.4fr" }}>
        <div className="card">
          <h4 className="font-bold text-[13.5px]">1:1 status breakdown</h4>
          <div className="flex items-center gap-4 mt-4">
            <div className="rounded-full grid place-items-center shrink-0" style={{ width: 96, height: 96, background: `conic-gradient(var(--status-good) 0 ${seg(total.c)}%, var(--blue) ${seg(total.c)}% ${seg(total.c) + seg(total.s)}%, var(--status-high) ${seg(total.c) + seg(total.s)}% 100%)` }} aria-hidden>
              <div className="rounded-full bg-white grid place-items-center" style={{ width: 66, height: 66 }}><div className="text-center"><div className="font-extrabold text-[16px] leading-none">{all}</div><div className="text-[10px] text-muted mt-0.5">total</div></div></div>
            </div>
            <div className="text-[12.5px] flex flex-col gap-1.5">
              {([["Completed", total.c, "var(--status-good)"], ["Scheduled", total.s, "var(--blue)"], ["Missed", total.m, "var(--status-high)"]] as const).map(([l, n, c]) => (
                <div key={l} className="flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{ background: c }} aria-hidden /><span className="flex-1 text-muted">{l}</span><span className="font-semibold w-8 text-right">{n}</span><span className="text-muted w-9 text-right">{all ? Math.round(seg(n)) : 0}%</span></div>
              ))}
            </div>
          </div>
        </div>
        <div className="card">
          <h4 className="font-bold text-[13.5px]">Completion rate by department</h4>
          <div className="mt-3 flex flex-col gap-2.5">
            {depts.length === 0 ? <div className="text-[13px] text-muted">No managers found.</div> : depts.map((d) => (
              <div key={d.dept} className="flex items-center gap-3 text-[12.5px]">
                <span className="w-[130px] truncate font-semibold">{d.dept}</span>
                <div className="flex-1 h-[6px] rounded-full bg-[var(--line)] overflow-hidden" aria-hidden><div className="h-full rounded-full" style={{ width: `${d.rate ?? 0}%`, background: d.rate == null ? "var(--line)" : d.rate >= 80 ? "var(--status-good)" : d.rate >= 50 ? "var(--status-elevated)" : "var(--status-high)" }} /></div>
                <span className="w-9 text-right font-semibold">{d.rate != null ? `${d.rate}%` : "—"}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="card !p-0">
          <div className="px-[18px] pt-4"><h4 className="font-bold text-[13.5px]">Managers with the lowest completion rate</h4></div>
          <Table
            rows={lowest}
            keyOf={(s) => s.manager_person_key}
            empty="No 1:1s recorded yet"
            cols={[
              { h: "Manager", cell: (s) => <><span className="font-semibold">{nameOf(s.manager_person_key)}</span><span className="sub">{deptOf(s.manager_person_key)}</span></> },
              { h: "Completion", cell: (s) => <Chip tone={toneOf(s.rate)}>{s.rate}%</Chip>, w: "110px" },
              { h: "Missed", cell: (s) => s.missed, w: "80px" },
              { h: "Upcoming", cell: (s) => s.scheduled, w: "90px" },
            ]}
          />
        </div>
      </div>
      <h3 className="text-[15px] font-bold mb-2">Every manager</h3>
      <Table
        rows={rows}
        keyOf={(s) => s.manager_person_key}
        empty="No 1:1s recorded yet"
        cols={[
          { h: "Manager", cell: (s) => <><span className="font-semibold">{nameOf(s.manager_person_key)}</span><span className="sub">{deptOf(s.manager_person_key)}</span></> },
          { h: "Completed", cell: (s) => s.completed, w: "110px" },
          { h: "Missed", cell: (s) => s.missed, w: "110px" },
          { h: "Upcoming", cell: (s) => s.scheduled, w: "110px" },
          { h: "Completion", cell: (s) => { const p = rate(s.completed, s.missed); return p == null ? <span className="text-muted">—</span> : <Chip tone={toneOf(p)}>{p}%</Chip>; }, w: "120px" },
        ]}
      />
    </div>
  );
}

function Mine() {
  const list = useOneOnOnes();
  const org = useOrgMe();
  const update = useUpdateOneOnOne();
  const create = useCreateOneOnOne();
  const agenda = useGenerateAgenda();
  const [error, setError] = useState<unknown>(null);
  const [f, setF] = useState({ report: "", when: "" });
  const [open, setOpen] = useState<string | null>(null);
  const reports = org.data?.direct_reports ?? [];
  return (
    <>
      <h3 className="text-[15px] font-bold mb-2">My 1:1s</h3>
      {error != null && <div className="mb-3"><ErrorBox error={error} /></div>}
      {reports.length > 0 && (
        <form
          className="card flex gap-3 items-end mb-3"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate({ report_person_key: f.report, scheduled_at: new Date(f.when).toISOString(), agenda: [] }, { onError: setError, onSuccess: () => setF({ report: "", when: "" }) });
          }}
        >
          <Field label="Report"><select className="input" required value={f.report} onChange={(e) => setF({ ...f, report: e.target.value })}><option value="">Choose…</option>{reports.map((p) => <option key={p.person_key} value={p.person_key}>{p.full_name ?? p.person_key}</option>)}</select></Field>
          <Field label="When"><input className="input" type="datetime-local" required value={f.when} onChange={(e) => setF({ ...f, when: e.target.value })} /></Field>
          <Button type="submit" disabled={create.isPending}>Schedule</Button>
        </form>
      )}
      {list.isPending ? (
        <Loading />
      ) : list.isError ? (
        <ErrorBox error={list.error} />
      ) : list.data.length === 0 ? (
        <Empty title="No 1:1s as a manager" body={reports.length === 0 ? "Your user is not linked to a person with direct reports." : "Schedule the first one above."} />
      ) : (
        <Table
          rows={list.data}
          keyOf={(o) => o.id}
          cols={[
            { h: "When", cell: (o: OneOnOne) => fmtDateTime(o.scheduled_at), w: "170px" },
            { h: "Report", cell: (o) => <span className="font-semibold">{reports.find((p) => p.person_key === o.report_person_key)?.full_name ?? o.report_person_key}</span> },
            { h: "Status", cell: (o) => <Chip tone={statusTone(o.status)}>{title(o.status)}</Chip>, w: "120px" },
            {
              h: "Agenda",
              w: "170px",
              cell: (o) => (
                <div className="flex items-center gap-2">
                  <span className="text-muted">{Array.isArray(o.agenda) && o.agenda.length ? `${o.agenda.length} items` : "—"}</span>
                  {Array.isArray(o.agenda) && o.agenda.length > 0 && <button className="link" onClick={() => setOpen(open === o.id ? null : o.id)}>{open === o.id ? "hide" : "view"}</button>}
                  {o.status === "scheduled" && (
                    <button className="link" disabled={agenda.isPending} onClick={() => agenda.mutate(o.id, { onError: setError, onSuccess: () => setOpen(o.id) })}>
                      {Array.isArray(o.agenda) && o.agenda.length ? "regenerate" : "✦ generate"}
                    </button>
                  )}
                </div>
              ),
            },
            { h: "Commitments", cell: (o) => <span className="text-muted">{Array.isArray(o.commitments) ? `${o.commitments.length}` : "—"}</span>, w: "110px" },
            {
              h: "",
              w: "220px",
              cell: (o) => o.status === "scheduled" ? (
                <div className="flex gap-1.5 justify-end">
                  <Button variant="light" className="!py-1 !px-2.5 !text-[12px]" onClick={() => update.mutate({ id: o.id, status: "completed" }, { onError: setError })}>Completed</Button>
                  <Button variant="light" className="!py-1 !px-2.5 !text-[12px]" onClick={() => update.mutate({ id: o.id, status: "missed" }, { onError: setError })}>Missed</Button>
                  <Button variant="light" className="!py-1 !px-2.5 !text-[12px]" onClick={() => update.mutate({ id: o.id, status: "cancelled" }, { onError: setError })}>Cancel</Button>
                </div>
              ) : null,
            },
          ]}
        />
      )}
      {open && list.data?.find((o) => o.id === open) && <Agenda items={(list.data.find((o) => o.id === open)!.agenda as AgendaItem[]) ?? []} />}
    </>
  );
}

function Agenda({ items }: { items: AgendaItem[] }) {
  return (
    <div className="card mt-3" style={{ background: "var(--gradient-flow-bg)" }}>
      <span className="kicker">✦ Agenda</span>
      <ol className="mt-2 space-y-2 list-decimal pl-5">
        {items.map((i, n) => (
          <li key={n} className="text-[13px]">
            <span className="font-semibold">{i.topic}</span>
            <span className="sub">{i.evidence}{i.source_signal ? ` · ${i.source_signal.replace(/_/g, " ")}` : ""}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}
